import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../shared/api/client";
import type {
  EnvironmentView,
  ReleaseDetail,
  ReleasePrerequisite,
  ReleasePrerequisiteCategory,
  ReleasePrerequisiteStatus,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";
import { PREREQUISITE_STATUS_LABELS } from "./releaseVocabulary";

const CATEGORIES: ReleasePrerequisiteCategory[] = [
  "ACCESS", "INFRASTRUCTURE", "DATABASE", "BACKUP", "ARTIFACT", "CONFIGURATION",
  "SECRETS", "DEPENDENCY", "FEATURE_FLAG", "MONITORING", "MAINTENANCE_WINDOW",
  "COMMUNICATION", "APPROVAL", "SECURITY", "CAPACITY", "EXTERNAL_DEPENDENCY", "OTHER",
];

/**
 * Adding a prerequisite, or recording what happened to one (§26).
 *
 * <p>One drawer for both because they are the same row seen at two moments, and splitting them
 * would mean two places to keep the category list correct.
 *
 * <p>Skipping and failing ask for a comment and will not save without one. Both are ways of
 * getting past a check without doing it, and a release that went out over an unexplained skipped
 * backup is precisely the record an incident review needs to be able to read.
 */
export function PrerequisiteDrawer({
  release,
  prerequisite,
  leg,
  open,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  prerequisite: ReleasePrerequisite | null;
  /**
   * The environment being worked on, when one leg of the journey is open (§63).
   *
   * <p>New prerequisites default to it. Somebody adding "restart the payment workers" while
   * standing inside the production leg means it for production, and making them say so a second
   * time in a dropdown is how it ends up untagged and blocking every sandbox deployment.
   */
  leg?: EnvironmentView | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = prerequisite != null;
  const [environmentId, setEnvironmentId] = useState("");

  /* §63: a prerequisite can belong to one leg of the journey rather than all of them. */
  const environments = useQuery({
    queryKey: ["release-environments"],
    queryFn: () => api.get<EnvironmentView[]>("/api/v1/environments"),
    enabled: open,
  });

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<ReleasePrerequisiteCategory>("ACCESS");
  const [description, setDescription] = useState("");
  const [mandatory, setMandatory] = useState(true);
  const [status, setStatus] = useState<ReleasePrerequisiteStatus>("NOT_STARTED");
  const [comment, setComment] = useState("");
  const [evidence, setEvidence] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (!open) {
      return;
    }
    setTitle(prerequisite?.title ?? "");
    setCategory(prerequisite?.category ?? "ACCESS");
    setDescription(prerequisite?.description ?? "");
    setMandatory(prerequisite?.mandatory ?? true);
    setStatus(prerequisite?.status ?? "NOT_STARTED");
    setComment(prerequisite?.completionComment ?? "");
    setEvidence(prerequisite?.evidenceReference ?? "");
    setEnvironmentId(prerequisite?.environment?.id ?? leg?.id ?? "");
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prerequisite]);

  const needsComment = status === "SKIPPED" || status === "FAILED";
  const needsEvidence = status === "COMPLETED" && (prerequisite?.evidenceRequired ?? false);
  const canSubmit =
    title.trim() !== ""
    && (!needsComment || comment.trim() !== "")
    && (!needsEvidence || evidence.trim() !== "");

  const submit = () =>
    void run(
      async () => {
        if (!editing) {
          await api.post(`/api/v1/releases/${release.id}/prerequisites`, {
            category,
            title: title.trim(),
            description: description.trim() === "" ? null : description.trim(),
            ownerId: null,
            mandatory,
            dueAt: null,
            environmentId: environmentId === "" ? null : environmentId,
            validationMethod: null,
            evidenceRequired: false,
            lockVersion: null,
          });
          return;
        }
        /*
         * Two writes, because they are two different acts on the server and each has its own
         * rules. Editing the wording is an edit; completing stamps who did it and when, and an
         * endpoint that could do both would be a way to record a completion nobody performed.
         *
         * The details go first and hand back a fresh lock version. Doing it the other way round
         * would make the second call fail its optimistic-lock check against a row the first had
         * just moved on.
         */
        let lockVersion = prerequisite.lockVersion;

        const detailsChanged =
          title.trim() !== prerequisite.title
          || category !== prerequisite.category
          || (description.trim() === "" ? null : description.trim())
            !== (prerequisite.description ?? null)
          || mandatory !== prerequisite.mandatory;

        if (detailsChanged) {
          const updated = await api.patch<ReleasePrerequisite>(
            `/api/v1/releases/${release.id}/prerequisites/${prerequisite.id}`,
            {
              category,
              title: title.trim(),
              description: description.trim() === "" ? null : description.trim(),
              ownerId: prerequisite.owner?.id ?? null,
              mandatory,
              dueAt: prerequisite.dueAt,
              environmentId: environmentId === "" ? null : environmentId,
              validationMethod: prerequisite.validationMethod,
              evidenceRequired: prerequisite.evidenceRequired,
              lockVersion,
            },
          );
          lockVersion = updated.lockVersion;
        }

        const statusChanged =
          status !== prerequisite.status
          || (comment.trim() === "" ? null : comment.trim())
            !== (prerequisite.completionComment ?? null)
          || (evidence.trim() === "" ? null : evidence.trim())
            !== (prerequisite.evidenceReference ?? null);

        if (statusChanged) {
          await api.put(
            `/api/v1/releases/${release.id}/prerequisites/${prerequisite.id}/status`,
            {
              status,
              comment: comment.trim() === "" ? null : comment.trim(),
              evidenceReference: evidence.trim() === "" ? null : evidence.trim(),
              lockVersion,
            },
          );
        }
      },
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editing ? "Update prerequisite" : "Add a prerequisite"}
      subtitle={editing ? prerequisite.title : "Something that must be true before this goes out."}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} loading={saving}>
            {saving ? "Saving…" : editing ? "Save" : "Add"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <Field label="What has to be true" required>
          <Input
            autoFocus
            value={title}
            placeholder="Production database snapshot taken"
            onChange={(event) => setTitle(event.target.value)}
          />
        </Field>
        <Field label="Category" required>
          <Select
            value={category}
            onChange={(event) =>
              setCategory(event.target.value as ReleasePrerequisiteCategory)
            }
          >
            {CATEGORIES.map((value) => (
              <option key={value} value={value}>
                {value.replace(/_/g, " ").toLowerCase()}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Applies to"
          hint="Leave as every environment unless this is only true of one leg — 'take a production snapshot' should not hold up a sandbox deployment."
        >
          <Select
            value={environmentId}
            onChange={(event) => setEnvironmentId(event.target.value)}
          >
            <option value="">Every environment</option>
            {(environments.data ?? []).map((environment) => (
              <option key={environment.id} value={environment.id}>
                {environment.name} only
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Detail" hint="Enough that somebody else could do it.">
          <Textarea
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm text-ink-800">
          <input
            type="checkbox"
            checked={mandatory}
            onChange={(event) => setMandatory(event.target.checked)}
          />
          Mandatory — the release cannot go until this is settled
        </label>

        {/* Status only once the item exists. A prerequisite cannot be completed in the same act
            that creates it — completing stamps who did it and when, and there is nobody to stamp
            before it has been written down. */}
        {editing && (
          <>
            <Field label="Status" required>
              <Select
                value={status}
                onChange={(event) =>
                  setStatus(event.target.value as ReleasePrerequisiteStatus)
                }
              >
                {Object.entries(PREREQUISITE_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              label={needsComment ? "Why" : "Note"}
              required={needsComment}
              hint={
                needsComment
                  ? "Skipping or failing a prerequisite is a decision. Say what happened."
                  : "Optional, and kept with the record."
              }
            >
              <Textarea
                rows={3}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />
            </Field>

            {prerequisite.evidenceRequired && (
              <Field
                label="Evidence"
                required={needsEvidence}
                hint="A link to the snapshot, screenshot or pipeline run."
              >
                <Input
                  value={evidence}
                  onChange={(event) => setEvidence(event.target.value)}
                />
              </Field>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}
